/**
 * Domain utilities for handling subdomains and root domains
 */

import { env } from '$env/dynamic/public';
import { setCookie } from '$lib/utils/cookies';

/**
 * Cookie max age in seconds. Defaults to 1 year (31536000 seconds).
 * Set PUBLIC_ORGANIZATION_COOKIE_MAX_AGE=0 for session-only cookies.
 * Configurable via PUBLIC_ORGANIZATION_COOKIE_MAX_AGE environment variable.
 */
const parsedMaxAge = env.PUBLIC_ORGANIZATION_COOKIE_MAX_AGE
	? parseInt(env.PUBLIC_ORGANIZATION_COOKIE_MAX_AGE, 10)
	: null;

export const COOKIE_MAX_AGE =
	parsedMaxAge !== null && !isNaN(parsedMaxAge) ? parsedMaxAge : 60 * 60 * 24 * 365;

/**
 * The configured app host (e.g., "internal.hessian.sh"). When set, this hostname
 * is treated as the root domain for subdomain logic, so "internal.hessian.sh"
 * is not considered a subdomain and "acme.internal.hessian.sh" resolves to
 * subdomain "acme" with root "internal.hessian.sh".
 */
function getConfiguredAppHost(): string | undefined {
	const raw = env.PUBLIC_APP_HOST;
	if (!raw) return undefined;
	return raw.split(':')[0];
}

/**
 * Extract the root domain from a hostname.
 * e.g., "acme.hessian.sh" → "hessian.sh"
 * e.g., "hessian.sh" → "hessian.sh"
 * e.g., "localhost" → "localhost"
 * e.g., "localhost:5173" → "localhost"
 *
 * When PUBLIC_APP_HOST is configured (e.g., "internal.hessian.sh"), that
 * hostname and any of its subdomains collapse to the configured value:
 * e.g., "acme.internal.hessian.sh" → "internal.hessian.sh"
 *
 * @param hostname The full hostname (without protocol)
 * @param appHost Override the configured app host (defaults to PUBLIC_APP_HOST)
 * @returns The root domain
 */
export function getRootDomain(hostname: string, appHost = getConfiguredAppHost()): string {
	// Remove port if present
	const hostWithoutPort = hostname.split(':')[0];

	// Handle localhost
	if (hostWithoutPort === 'localhost' || hostWithoutPort === '127.0.0.1') {
		return hostWithoutPort;
	}

	// If an app host is configured, only treat that exact host (and its subdomains)
	// as multi-tenant. Any other hostname is a deploy-preview / workers.dev URL etc.
	// and should be reported as its own root so middleware doesn't redirect off it
	// (e.g. parsing "*.hessian.workers.dev" via dot-split would give "workers.dev").
	if (appHost) {
		if (hostWithoutPort === appHost || hostWithoutPort.endsWith(`.${appHost}`)) {
			return appHost;
		}
		return hostWithoutPort;
	}

	// Split by dots
	const parts = hostWithoutPort.split('.');

	// If we have 2 or fewer parts, it's already the root domain
	// e.g., "hessian.sh" or "localhost"
	if (parts.length <= 2) {
		return hostWithoutPort;
	}

	// Return the last two parts as the root domain
	// e.g., "acme.hessian.sh" → "hessian.sh"
	return parts.slice(-2).join('.');
}

/**
 * Get the cookie domain (with leading dot) for cross-subdomain cookie sharing.
 * Returns undefined for localhost.
 *
 * Unlike `getRootDomain`, this ignores `PUBLIC_APP_HOST` and always resolves
 * to the registrable parent (last two labels) so the cookie reaches sibling
 * subdomains like `api.hessian.sh` — not just subdomains of the app host.
 *
 * @param hostname The full hostname
 * @returns The cookie domain (e.g., ".hessian.sh") or undefined for localhost
 */
export function getCookieDomain(hostname: string): string | undefined {
	const hostWithoutPort = hostname.split(':')[0];

	if (hostWithoutPort === 'localhost' || hostWithoutPort === '127.0.0.1') {
		return undefined;
	}

	const parts = hostWithoutPort.split('.');
	if (parts.length <= 2) {
		return `.${hostWithoutPort}`;
	}

	return `.${parts.slice(-2).join('.')}`;
}

/**
 * Extract the current subdomain from a hostname.
 * e.g., "acme.hessian.sh" → "acme"
 * e.g., "hessian.sh" → null
 * e.g., "localhost" → null
 *
 * When PUBLIC_APP_HOST is configured (e.g., "internal.hessian.sh"):
 * - "internal.hessian.sh" → null
 * - "acme.internal.hessian.sh" → "acme"
 *
 * @param hostname The full hostname
 * @param appHost Override the configured app host (defaults to PUBLIC_APP_HOST)
 * @returns The subdomain or null if on the root domain
 */
export function getSubdomain(
	hostname: string,
	appHost = getConfiguredAppHost()
): string | null {
	// Remove port if present
	const hostWithoutPort = hostname.split(':')[0];

	// Handle localhost
	if (hostWithoutPort === 'localhost' || hostWithoutPort === '127.0.0.1') {
		return null;
	}

	// If an app host is configured, resolve relative to it. Hostnames that are
	// neither the app host nor a subdomain of it are deploy-preview URLs
	// (workers.dev, *.pages.dev, etc.) — they have no meaningful "subdomain"
	// in the multi-tenant sense, so return null instead of falling through to
	// the generic dot-split logic (which would treat e.g.
	// "*.hessian.workers.dev" as if "workers.dev" were the registrable root).
	if (appHost) {
		if (hostWithoutPort === appHost) {
			return null;
		}
		if (hostWithoutPort.endsWith(`.${appHost}`)) {
			const sub = hostWithoutPort.slice(0, -(appHost.length + 1));
			return sub || null;
		}
		return null;
	}

	const parts = hostWithoutPort.split('.');

	// If we have 2 or fewer parts, there's no subdomain
	if (parts.length <= 2) {
		return null;
	}

	// Return all parts except the last two (the root domain)
	// This handles multi-level subdomains like "team.acme.hessian.sh" → "team.acme"
	return parts.slice(0, -2).join('.');
}

/**
 * Build a URL for a specific organization subdomain.
 *
 * @param slug The organization slug (null for personal org / root domain)
 * @param rootDomain The root domain (e.g., "hessian.sh")
 * @param pathname The pathname to navigate to (defaults to current path or "/dashboard")
 * @param protocol The protocol to use (defaults to "https")
 * @returns The full URL
 */
export function buildSubdomainUrl(
	slug: string | null | undefined,
	rootDomain: string,
	pathname: string = '/dashboard',
	protocol: string = 'https'
): string {
	// For localhost, we can't use subdomains, so just return the path
	if (rootDomain === 'localhost' || rootDomain === '127.0.0.1') {
		return pathname;
	}

	const host = slug ? `${slug}.${rootDomain}` : rootDomain;
	return `${protocol}://${host}${pathname}`;
}

/**
 * Check if we're currently on the correct subdomain for an organization.
 *
 * When `PUBLIC_APP_HOST` is configured and the current host is neither
 * localhost nor the app host (nor a subdomain of it), treat the host as
 * "already correct" — preview/workers.dev URLs are not tenantable, so
 * callers like `navigateToOrgSubdomain` must not try to redirect to
 * `slug.<preview-host>`.
 *
 * @param currentHostname The current hostname
 * @param orgSlug The organization slug (null for personal org)
 * @param appHost Override the configured app host (defaults to PUBLIC_APP_HOST)
 * @returns True if we're on the correct subdomain
 */
export function isOnCorrectSubdomain(
	currentHostname: string,
	orgSlug: string | null | undefined,
	appHost = getConfiguredAppHost()
): boolean {
	const hostWithoutPort = currentHostname.split(':')[0];
	const isLocalhost = hostWithoutPort === 'localhost' || hostWithoutPort === '127.0.0.1';

	if (
		appHost &&
		!isLocalhost &&
		hostWithoutPort !== appHost &&
		!hostWithoutPort.endsWith(`.${appHost}`)
	) {
		return true;
	}

	const currentSubdomain = getSubdomain(currentHostname, appHost);

	// Personal org (no slug) should be on root domain (no subdomain)
	if (!orgSlug) {
		return currentSubdomain === null;
	}

	// Team org should be on its subdomain
	return currentSubdomain === orgSlug;
}

/**
 * Set the organization cookie and navigate to the correct subdomain if needed.
 * Returns true if a cross-subdomain navigation was initiated, false if already
 * on the correct subdomain (no navigation needed).
 *
 * This is a client-side only function (uses `window`).
 *
 * @param orgId The organization ID to persist in the cookie
 * @param orgSlug The organization slug (null for personal org / root domain)
 * @param cookieName The cookie name to use for persisting the org ID
 * @param cookieMaxAge The max age of the cookie in seconds
 * @param pathname The pathname to navigate to (defaults to current path)
 */
export function navigateToOrgSubdomain(
	orgId: string,
	orgSlug: string | null,
	cookieName: string,
	cookieMaxAge?: number,
	pathname?: string
): boolean {
	const currentHostname = window.location.hostname;
	const rootDomain = getRootDomain(currentHostname);
	const isLocalhost = rootDomain === 'localhost' || rootDomain === '127.0.0.1';

	if (isLocalhost || isOnCorrectSubdomain(currentHostname, orgSlug)) {
		return false;
	}

	persistOrgCookie(orgId, cookieName, cookieMaxAge);

	const protocol = window.location.protocol.replace(':', '');
	window.location.href = buildSubdomainUrl(
		orgSlug,
		rootDomain,
		pathname ?? window.location.pathname,
		protocol
	);

	return true;
}

/**
 * Write the organization cookie for the current host. Used on the
 * same-host branch (localhost / already-on-the-right-subdomain) of the
 * team switcher, where `navigateToOrgSubdomain` bails out without touching
 * the cookie. The TeamSwitcher needs to set it synchronously so a
 * follow-up `invalidateAll` SSR fetch reads the new value.
 *
 * Returns the cookie value that was written so callers can pass it
 * straight into invalidation logic.
 */
export function persistOrgCookie(
	orgId: string,
	cookieName: string,
	cookieMaxAge?: number
): string {
	const cookieDomain = getCookieDomain(window.location.hostname);
	setCookie(cookieName, orgId, {
		path: '/',
		domain: cookieDomain,
		sameSite: 'Lax',
		secure: true,
		maxAge: cookieMaxAge
	});
	return orgId;
}
